import React, {useEffect} from "react";
import {useAuthContext} from "../../contexts/AuthContext";
import {AppSidebarNav} from "./AppSidebarNav";
import {Outlet} from "react-router-dom";

import {Navigate, useLocation} from "react-router";
import Box from "@mui/material/Box";
import {useUserContext} from "../../contexts/UserContext";
import './HomePage.scss'
import useDidMountEffect from "../../hooks/useDidMountEffect";

import { useMediaQuery } from 'react-responsive'
import {useUIContext} from "../../contexts/UIContext";
import {AppMobileNav} from "./AppMobileNav";
import Stack from "@mui/material/Stack";
import {useModalsStore} from "../../stores/modalsStore";
import {useShallow} from "zustand/react/shallow";
import {useInterval} from "../../hooks/useInterval";
import {reloadIfStale} from "../../utils/versionCheck";
import {useThemeStore} from "../../stores/themeStore";
import {useNotificationPolling} from "../Notifications/hooks/useNotificationPolling";

export const mediaQueryForMobile = '(max-width: 1224px)'

export const HomePage = () => {
    const { isAuthenticated } = useAuthContext()
    const { updateProfile, profileState, isFeatureFlagEnabled } = useUserContext()
    const { uiState } = useUIContext()
    let location = useLocation();

    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })

    // Reflect the current theme on <html> so global selectors (page roots,
    // future modal/portal rules, scrollbar color-scheme) can react. The
    // per-page `data-theme={theme}` on each Box still drives the scoped
    // token blocks under #ListDealsPage / #CalculatePage / #ProfilePage,
    // but having it on the documentElement is what makes the rest of the
    // app (sidebar, modals, etc.) able to follow once they're converted.
    const theme = useThemeStore((state) => state.theme)
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme)
    }, [theme])

    // The single notification poller for the whole app. It lives here, in the
    // authenticated shell, so the bell's badge stays current on every page —
    // and so there is exactly one of it. Mounting it inside the dashboard or
    // the bell would give us a poller per mount and stop updates the moment
    // the user navigated away.
    useNotificationPolling(isFeatureFlagEnabled('notifications'))

    useDidMountEffect(function updatePagesVisited(){
        const pagesVisited = profileState?.pages_visited
        if (pagesVisited) {
            const pageVisited = location.pathname
            const lastPageVisited = pagesVisited[0]
            if (pageVisited !== lastPageVisited) {
                let newPagesVisited = structuredClone(profileState.pages_visited)
                newPagesVisited.unshift(pageVisited)
                newPagesVisited = newPagesVisited.slice(0,20)
                // Only this field: a full-profile PATCH re-sends stale values
                // (e.g. tutorial progress saved since).
                updateProfile({pages_visited: newPagesVisited})
            }
        }
    }, [location]);

    // Reload when a newer frontend build is deployed. We compare our running
    // build (REACT_APP_VERSION, frozen into this bundle at build time) against
    // /version.json, which is written at build time and served alongside the
    // bundle — so if it reports a new version, that build is actually available
    // and the reload will pick it up (unlike the old backend comparison, which
    // deploys separately and could report a version the CDN can't serve yet).
    useInterval(function checkClientVersion(){
        if (process.env.REACT_APP_ENV === 'local') return

        fetch(`/version.json?t=${Date.now()}`, {cache: 'no-store'})
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => reloadIfStale({
                deployed: data?.version,
                running: process.env.REACT_APP_VERSION,
                storage: sessionStorage,
                reload: () => window.location.reload(true),
            }))
            .catch(() => {})   // transient network error — retry next tick
    }, 300000)

    const {
        modals,
        closeModal,
        closeAllModals,
    } = useModalsStore(
        useShallow((state) => ({
            modals: state.modals,
            closeModal: state.closeModal,
            closeAllModals: state.closeAllModals,
        }))
    )

    // Close any open modals (and their backdrop overlay) whenever the route
    // changes. Store-based modals render at this persistent HomePage level, so
    // without this they linger over the destination page after navigating via
    // the side-tab menu or any other navigation path.
    useDidMountEffect(function closeModalsOnNavigation() {
        closeAllModals()
    }, [location.pathname])

    if (!isAuthenticated) {
        return <Navigate to={'/login'} replace={true}/>
    }

    const {sideNavDrawerWidth} = uiState

    const isStaging = process.env.REACT_APP_ENV === 'staging'

    return (
        <div
            style={{ flexDirection: isTabletOrMobile ? 'column' : 'row'}}
            id={"HomePage"}
        >
            {
                false && isStaging &&
                <Stack
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    className={'staging-warning'}
                >
                    <span>Welcome to Staging where nothing matters</span>
                </Stack>
            }
            <Box
                style={{
                    width: isTabletOrMobile ? '100%' : `${sideNavDrawerWidth}px`
                }}
            >
                {
                    isTabletOrMobile ? <AppMobileNav/> : <AppSidebarNav />
                }
            </Box>
            <Box
                component="main"
                style={{
                    flexGrow: 1,
                    width: isTabletOrMobile ? '100%' : `calc(100% - ${sideNavDrawerWidth}px)`,
                    marginTop: isTabletOrMobile ? '56px' : '',
                    // How far down the viewport main starts. Same boolean as
                    // marginTop so the two can't drift, and pages needing a
                    // definite height (.crm-layout) subtract it from 100vh
                    // rather than restating the breakpoint in SCSS.
                    '--app-top-offset': isTabletOrMobile ? '56px' : '0px',
                }}
            >
                <Outlet/>
            </Box>

            {
                modals.map((modal, index) => {
                    const { element, props } = modal

                    return React.createElement(
                        element,
                        {
                            ...props,
                            key: index,
                            index,
                            open: true,
                            isOpen: true,
                            setIsOpen: () => closeModal(index)
                        },
                        null,
                        index
                    )
                })
            }
        </div>
    )
}
